import { useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { trpc, trpcClient } from '@/lib/trpc';
import { BasketProvider } from '@/lib/cart';
import { AuthProvider } from '@/lib/auth';
import { ProtectedRoute, AdminRoute } from '@/components/RouteGuards';

import Navbar            from '@/components/Navbar';
import HomePage          from '@/pages/HomePage';
import CoursesPage       from '@/pages/CoursesPage';
import BookingPage       from '@/pages/BookingPage';
import MyCoursesPage     from '@/pages/MyCoursesPage';
import CoursePlayerPage  from '@/pages/CoursePlayerPage';
import BasketPage        from '@/pages/BasketPage';
import LoginPage         from '@/pages/LoginPage';
import UnauthorizedPage  from '@/pages/UnauthorizedPage';
import EnvLayout            from '@/pages/environmental/EnvLayout';
import EnvironmentalHub     from '@/pages/environmental/EnvironmentalHub';
import EnvironmentalAdmin   from '@/pages/environmental/EnvironmentalAdmin';
import EnvironmentalGamePage from '@/pages/environmental/EnvironmentalGamePage';
import { LOGIN_ENABLED } from '@/lib/features';

// Admin
import AdminShell       from '@/pages/admin/AdminShell';
import AdminDashboard   from '@/pages/admin/AdminDashboard';
import AdminCourses     from '@/pages/admin/AdminCourses';
import AdminInstructors from '@/pages/admin/AdminInstructors';
import AdminCategories  from '@/pages/admin/AdminCategories';
import AdminSessions    from '@/pages/admin/AdminSessions';
import AdminBookings  from '@/pages/admin/AdminBookings';
import AdminOrders    from '@/pages/admin/AdminOrders';
import AdminQuestions from '@/pages/admin/AdminQuestions';
import AdminUsers     from '@/pages/admin/AdminUsers';
import AdminAnalytics from '@/pages/admin/AdminAnalytics';

/** The site navbar is hidden on the standalone /environmental area. */
function SiteNavbar() {
  const { pathname } = useLocation();
  return pathname.startsWith('/environmental') ? null : <Navbar />;
}

export default function App() {
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: { queries: { retry: 1, staleTime: 10_000 } },
  }));

  return (
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        {/*
          AuthProvider must be inside QueryClientProvider so it can call
          trpc.auth.me internally. It provides useAuth() to the whole tree.
        */}
        <AuthProvider>
          <BasketProvider>
            <BrowserRouter>
              <div className="min-h-screen bg-black font-sans">
                <SiteNavbar />
                <Routes>
                  {/* ── Public routes ── */}
                  <Route path="/"              element={<HomePage />} />
                  <Route path="/courses"       element={<CoursesPage />} />
                  <Route path="/login"         element={LOGIN_ENABLED ? <LoginPage /> : <Navigate to="/" replace />} />
                  <Route path="/unauthorized"  element={<UnauthorizedPage />} />

                  {/* ── Environmental area (reached by typing /environmental — not linked from the site) ── */}
                  <Route path="/environmental" element={<EnvLayout />}>
                    <Route index         element={<EnvironmentalHub />} />
                    <Route path="admin"  element={<EnvironmentalAdmin />} />
                    <Route path=":slug"  element={<EnvironmentalGamePage />} />
                  </Route>

                  {/* ── Protected routes (must be logged in) ── */}
                  <Route path="/booking" element={
                    <ProtectedRoute><BookingPage /></ProtectedRoute>
                  }/>
                  <Route path="/my-courses" element={
                    <ProtectedRoute><MyCoursesPage /></ProtectedRoute>
                  }/>
                  <Route path="/courses/:id/watch" element={
                    <ProtectedRoute><CoursePlayerPage /></ProtectedRoute>
                  }/>
                  <Route path="/basket" element={
                    <ProtectedRoute><BasketPage /></ProtectedRoute>
                  }/>

                  {/* ── Admin routes (must be logged in + role === 'admin') ── */}
                  <Route path="/admin" element={
                    <AdminRoute><AdminShell /></AdminRoute>
                  }>
                    <Route index             element={<AdminDashboard />} />
                    <Route path="courses"     element={<AdminCourses />} />
                    <Route path="instructors" element={<AdminInstructors />} />
                    <Route path="majors"      element={<AdminCategories />} />
                    <Route path="sessions"    element={<AdminSessions />} />
                    <Route path="bookings"  element={<AdminBookings />} />
                    <Route path="orders"    element={<AdminOrders />} />
                    <Route path="questions" element={<AdminQuestions />} />
                    <Route path="users"     element={<AdminUsers />} />
                    <Route path="analytics" element={<AdminAnalytics />} />
                  </Route>
                </Routes>
              </div>
            </BrowserRouter>
          </BasketProvider>
        </AuthProvider>
      </QueryClientProvider>
    </trpc.Provider>
  );
}

